"use client";

import { useEffect, useRef, useState } from "react";
import { content } from "@/config/content";
import { useCart } from "@/components/shared/CartProvider";
import ImagePlaceholder from "@/components/shared/ImagePlaceholder";
import { useSiteSettings } from "@/lib/useSiteSettings";
import { useBodyScrollLock } from "@/lib/useBodyScrollLock";
import { useFocusTrap } from "@/lib/useFocusTrap";
import ShareButton from "@/components/shared/ShareButton";
import { buildSharePayload } from "@/lib/shareUrl";
import { trackAddToCart } from "@/lib/analytics";

export default function ArtworkQuickView({ artwork, onClose }) {
  const { addItem, items } = useCart();
  const backdropRef = useRef(null);
  const panelRef = useRef(null);
  const scrollRef = useRef(null);
  const [canScroll, setCanScroll] = useState(false);
  const isAvailable = artwork?.status === "available";
  const alreadyInCart = items.some((item) => item.id === artwork?.id);
  const { settings } = useSiteSettings("pricing");
  // Phase 3.7 — artwork copy from CMS
  const { settings: art } = useSiteSettings("artwork");
  const fileDownload = Number(settings.price_file_download || 2500);
  const displayPrice =
    Number(artwork?.price) > 0 ? Number(artwork.price) : fileDownload;

  useBodyScrollLock(!!artwork);
  useFocusTrap({
    isActive: !!artwork,
    containerRef: panelRef,
    onEscape: onClose,
  });

  // Detect if content is scrollable
  useEffect(() => {
    if (!artwork || !scrollRef.current) return;
    const el = scrollRef.current;
    const check = () => setCanScroll(el.scrollHeight > el.clientHeight + 8);
    check();
    const onScroll = () => {
      const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 16;
      setCanScroll(!nearBottom);
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [artwork]);

  if (!artwork) return null;

  const handleAdd = () => {
    addItem(artwork);
    trackAddToCart({ id: artwork.id, name: artwork.name }, displayPrice);
    onClose();
  };

  return (
    <div
      ref={backdropRef}
      role="dialog"
      aria-modal="true"
      aria-label={artwork.name || art.artwork_preview_label || "作品預覽"}
      className="fixed inset-0 z-[70] flex items-end md:items-center justify-center"
      onClick={(e) => {
        if (e.target === backdropRef.current) onClose();
      }}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 animate-fade-in"
        style={{
          backgroundColor: "rgba(0,0,0,0.5)",
          backdropFilter: "blur(4px)",
        }}
      />

      {/* Panel */}
      <div
        ref={panelRef}
        className="relative w-full md:w-[calc(100%-32px)] max-w-[420px] max-h-[92vh] md:max-h-[85vh] flex flex-col animate-slide-up md:animate-fade-in-up rounded-t-[16px] md:rounded-[16px]"
        style={{
          backgroundColor: "var(--color-bg)",
          boxShadow: "0 4px 40px rgba(0,0,0,0.2)",
          overscrollBehavior: "contain",
        }}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 z-10 w-11 h-11 flex items-center justify-center rounded-full"
          style={{ backgroundColor: "rgba(0,0,0,0.4)", color: "#fff" }}
          aria-label="關閉"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
          >
            <line x1="6" y1="6" x2="18" y2="18" />
            <line x1="18" y1="6" x2="6" y2="18" />
          </svg>
        </button>

        {/* Scrollable content area */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto min-h-0">
          {/* Image with gradient overlay for title */}
          <div className="relative">
            <ImagePlaceholder
              name={artwork.name}
              image={artwork.image}
              artworkId={artwork.id}
              aspectRatio="4/5"
              className="w-full"
            />
            {/* 已帶走對角章印 — 圖上 28% 處避開底部 title gradient */}
            {!isAvailable && (
              <div
                className="absolute inset-0 flex items-start justify-center pointer-events-none"
                style={{ paddingTop: "28%" }}
                aria-hidden="true"
              >
                <div
                  className="select-none"
                  style={{
                    transform: "rotate(-12deg)",
                    padding: "10px 28px",
                    border: "3.5px solid rgba(185,28,28,0.9)",
                    borderRadius: "6px",
                    color: "rgba(185,28,28,0.95)",
                    fontSize: "22px",
                    fontWeight: 700,
                    letterSpacing: "0.2em",
                    fontFamily: "var(--font-heading)",
                    backgroundColor: "rgba(255,255,255,0.82)",
                    backdropFilter: "blur(2px)",
                    WebkitBackdropFilter: "blur(2px)",
                    boxShadow:
                      "0 3px 14px rgba(0,0,0,0.25), inset 0 0 0 1px rgba(255,255,255,0.5)",
                  }}
                >
                  已帶走 ♡
                </div>
              </div>
            )}
            {/* Gradient overlay with name on image */}
            <div
              className="absolute bottom-0 left-0 right-0 p-5 pb-4"
              style={{
                background:
                  "linear-gradient(to top, rgba(0,0,0,0.65) 0%, rgba(0,0,0,0.3) 60%, transparent 100%)",
              }}
            >
              {isAvailable ? (
                <span
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium mb-2"
                  style={{
                    backgroundColor: "var(--color-available)",
                    color: "var(--color-availableText, #fff)",
                    borderRadius: "var(--radius-full)",
                  }}
                >
                  <span
                    className="w-1.5 h-1.5 rounded-full animate-pulse"
                    style={{
                      backgroundColor: "var(--color-availableText, #fff)",
                    }}
                  />
                  {art.artwork_available_badge || content.gallery.availableLabel}
                </span>
              ) : (
                <span
                  className="inline-block px-2.5 py-1 text-xs mb-2"
                  style={{
                    backgroundColor: "rgba(255,255,255,0.25)",
                    color: "#fff",
                    borderRadius: "var(--radius-full)",
                  }}
                >
                  {content.gallery.claimedLabel}
                </span>
              )}
              <h2
                className="text-xl md:text-2xl font-bold text-white"
                style={{
                  fontFamily: "var(--font-heading)",
                  textShadow: "0 1px 4px rgba(0,0,0,0.3)",
                }}
              >
                {artwork.name}
              </h2>
            </div>
          </div>

          {/* Info */}
          <div className="p-5 md:p-6">
            {artwork.description && (
              <p
                className="text-sm leading-relaxed mb-4"
                style={{ color: "var(--color-textSecondary)" }}
              >
                {artwork.description}
              </p>
            )}

            {/* Suggested positions */}
            {artwork.suggestedPositions?.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-2">
                {artwork.suggestedPositions.map((pos) => (
                  <span
                    key={pos}
                    className="text-xs px-2.5 py-0.5"
                    style={{
                      backgroundColor: "var(--color-bgSecondary)",
                      color: "var(--color-textSecondary)",
                      borderRadius: "var(--radius-full)",
                    }}
                  >
                    {pos}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Scroll hint — fades when at bottom */}
        <div
          className="absolute left-0 right-0 bottom-[72px] h-8 pointer-events-none transition-opacity duration-300"
          style={{
            background: `linear-gradient(to top, var(--color-bg), transparent)`,
            opacity: canScroll ? 1 : 0,
          }}
        />

        {/* Sticky bottom: Price + Share + primary CTA */}
        <div
          className="flex-shrink-0 flex flex-col gap-3 px-5 py-4"
          style={{
            borderTop: "1px solid var(--color-cardBorder)",
            backgroundColor: "var(--color-bg)",
          }}
        >
          <div className="flex items-center justify-between gap-3">
            <span
              className="text-xl font-bold"
              style={{ color: "var(--color-accent)" }}
            >
              {content.gallery.pricePrefix}
              {displayPrice.toLocaleString()}
            </span>
            <ShareButton
              payload={buildSharePayload({ artwork, kind: "artwork" })}
              variant="pill"
              size="sm"
              placement="modal"
              emoji="🐾"
              label={art.artwork_share_button || "分享這張"}
            />
          </div>

          {isAvailable ? (
            alreadyInCart ? (
              <span
                className="w-full px-5 py-3 text-sm font-medium text-center"
                style={{
                  backgroundColor: "var(--color-bgSecondary)",
                  color: "var(--color-textMuted)",
                  borderRadius: "var(--radius-full)",
                }}
              >
                {art.artwork_in_cart || "已在購物車中"}
              </span>
            ) : (
              <button
                onClick={handleAdd}
                className="w-full flex items-center justify-center gap-2 px-5 py-3 text-sm font-medium text-white transition-transform active:scale-95"
                style={{
                  backgroundColor: "var(--color-accent)",
                  borderRadius: "var(--radius-full)",
                }}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="9" cy="21" r="1" />
                  <circle cx="20" cy="21" r="1" />
                  <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
                </svg>
                {art.artwork_add_to_cart || "加入購物車"}
              </button>
            )
          ) : (
            <span
              className="w-full px-5 py-3 text-sm text-center"
              style={{
                backgroundColor: "var(--color-bgSecondary)",
                color: "var(--color-textMuted)",
                borderRadius: "var(--radius-full)",
              }}
            >
              {content.gallery.claimedButton}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
